import React, { useEffect, useState } from 'react';
import { ExhibitStatus, SearchFilters } from '../../../types/models';
import { cn } from '../../../utils/cn';

interface ExhibitFiltersProps {
    onFiltersChange: (filters: SearchFilters) => void;
    initialFilters?: SearchFilters;
    className?: string;
}

/**
 * Exhibit filters component for filtering the exhibit list
 *
 * Features:
 * - Search input with debounce
 * - Status filter dropdown
 * - Active filter indicators
 * - Clear filters button
 */
const ExhibitFilters = React.memo(({ onFiltersChange, initialFilters, className }: ExhibitFiltersProps) => {
    const [filters, setFilters] = useState<SearchFilters>(initialFilters || {});
    const [searchTerm, setSearchTerm] = useState(initialFilters?.query || '');
    const [searchTimeout, setSearchTimeout] = useState<number | null>(null);

    // Apply debounced search
    useEffect(() => {
        // Clear any existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set a new timeout to update filters after debounce period
        const timeout = setTimeout(() => {
            if (filters.query !== searchTerm) {
                setFilters((prev) => ({ ...prev, query: searchTerm || undefined }));
            }
        }, 300) as unknown as number;

        setSearchTimeout(timeout);

        // Cleanup function
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTerm, filters.query]);

    // Update parent component when filters change
    useEffect(() => {
        onFiltersChange(filters);
    }, [filters, onFiltersChange]);

    // Handle status filter change
    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const status = e.target.value === 'all' ? undefined : e.target.value;
        setFilters((prev) => ({ ...prev, status }));
    };

    // Reset all filters
    const clearFilters = () => {
        setSearchTerm('');
        setFilters({});
    };

    // Check if any filters are active
    const hasActiveFilters = !!filters.query || !!filters.status;

    return (
        <div className={cn('flex flex-col gap-4', className)}>
            {/* Search and controls */}
            <div className="flex flex-col gap-3 sm:flex-row">
                {/* Search input */}
                <div className="relative flex-grow">
                    <input
                        type="text"
                        placeholder="Search exhibits..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="focus:border-primary focus:ring-primary w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 focus:ring-1 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                    <span className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </span>
                </div>

                {/* Status filter */}
                {/* <div className="w-full sm:w-auto">
                    <select
                        value={filters.status || 'all'}
                        onChange={handleStatusChange}
                        className="focus:border-primary focus:ring-primary w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:ring-1 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    >
                        <option value="all">All Statuses</option>
                        <option value="on_display">On Display</option>
                        <option value="in_storage">In Storage</option>
                        <option value="on_loan">On Loan</option>
                    </select>
                </div> */}

                {/* Clear filters button */}
                {hasActiveFilters && (
                    <button
                        onClick={clearFilters}
                        className="text-primary hover:bg-primary hover:bg-opacity-10 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            {/* Active filters */}
            {hasActiveFilters && (
                <div className="mt-2 flex flex-wrap gap-2">
                    {filters.query && (
                        <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm dark:bg-gray-700">
                            <span className="mr-1">Search:</span>
                            <span className="font-medium">{filters.query}</span>
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setFilters((prev) => ({ ...prev, query: undefined }));
                                }}
                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                aria-label="Remove search filter"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    )}

                    {filters.status && (
                        <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm dark:bg-gray-700">
                            <span className="mr-1">Status:</span>
                            <span className="font-medium capitalize">{filters.status}</span>
                            <button
                                onClick={() => {
                                    setFilters((prev) => ({ ...prev, status: undefined }));
                                }}
                                className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                aria-label="Remove status filter"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default ExhibitFilters;
